import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { atsFetch, AtsClient, AtsPerson, AtsPlacement, AtsRole, CLIENT_STAGES, ROLE_STATUSES, fmtMoney, fmtDate } from './api';
import { Modal, Field, inputCls, btnPrimary, btnGhost, StatusBadge, TimelinePanel, TasksPanel, NotesPanel } from './shared';

// The client record, Spott-informed: main tabs (Roles | Contacts |
// Placements) + a persistent right panel (Details | Activity). Details carry
// the ENRICHED company data the mapped market already holds — industry,
// size, founded, revenue, funding, specialties, description — resolved by
// the stored company link or the client's domain.
interface CompanyProfile {
  id: number; project_id: number; project_name: string;
  company_name: string | null; industry: string | null; subindustry: string | null;
  description: string | null; employee_count: number | null; company_size_formatted: string | null;
  founded: string | number | null; annual_revenue: string | null; funding_range: string | null;
  specialties: string | null; logo_url: string | null; website: string | null;
  linkedin_url: string | null; locality: string | null; country: string | null;
  follower_count: number | null;
}

const TABS = ['Roles', 'Contacts', 'Placements', 'Notes'] as const;

const DetailRow: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="py-1.5">
    <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
    <div className="text-sm text-gray-700 break-words">{children}</div>
  </div>
);

const AtsClientDetail: React.FC = () => {
  const { clientId } = useParams<{ clientId: string }>();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [client, setClient] = useState<AtsClient | null>(null);
  const [company, setCompany] = useState<CompanyProfile | null>(null);
  const [roles, setRoles] = useState<AtsRole[]>([]);
  const [contacts, setContacts] = useState<AtsPerson[]>([]);
  const [placements, setPlacements] = useState<AtsPlacement[]>([]);
  const [tab, setTab] = useState<(typeof TABS)[number]>('Roles');
  const [panelTab, setPanelTab] = useState<'Details' | 'Activity'>('Details');
  const [error, setError] = useState('');
  const [showRole, setShowRole] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [roleForm, setRoleForm] = useState({ title: '', location: '', employmentType: 'permanent', feeType: 'percent', feeValue: '', salaryMin: '', salaryMax: '', status: 'open', description: '' });
  const [contactForm, setContactForm] = useState({ name: '', contactTitle: '', email: '', phone: '', linkedinUrl: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!token || !clientId) return;
    atsFetch(token, `/clients/${clientId}`)
      .then((d) => { setClient(d.client); setCompany(d.companyProfile); setRoles(d.roles); setContacts(d.contacts); setPlacements(d.placements); })
      .catch((e) => setError(e.message));
  }, [token, clientId]);

  useEffect(load, [load]);
  useEffect(() => { setTab('Roles'); setPanelTab('Details'); }, [clientId]);

  const specialties = useMemo(() => {
    const v: any = company?.specialties;
    if (!v) return [];
    const arr = Array.isArray(v) ? v : String(v).split(',');
    return arr
      .map((s: any) => (typeof s === 'string' ? s : String(s?.name || '')))
      .map((s: string) => s.trim()).filter(Boolean).slice(0, 14);
  }, [company]);

  const patchClient = async (patch: any) => {
    if (!token || !client) return;
    const d = await atsFetch(token, `/clients/${client.id}`, { method: 'PATCH', body: JSON.stringify(patch) }).catch(() => null);
    if (d) setClient(d.client);
  };

  const createRole = async () => {
    if (!token || !client || !roleForm.title.trim()) return;
    setSaving(true);
    try {
      const d = await atsFetch(token, '/roles', {
        method: 'POST',
        body: JSON.stringify({
          clientId: client.id, title: roleForm.title, location: roleForm.location || null,
          employmentType: roleForm.employmentType, feeType: roleForm.feeType,
          feeValue: roleForm.feeValue === '' ? null : Number(roleForm.feeValue),
          salaryMin: roleForm.salaryMin === '' ? null : Number(roleForm.salaryMin),
          salaryMax: roleForm.salaryMax === '' ? null : Number(roleForm.salaryMax),
          status: roleForm.status, description: roleForm.description || null,
        }),
      });
      setShowRole(false);
      navigate(`/ats/roles/${d.role.id}`);
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const addContact = async () => {
    if (!token || !client || !contactForm.name.trim()) return;
    setSaving(true);
    try {
      await atsFetch(token, `/clients/${client.id}/contacts`, { method: 'POST', body: JSON.stringify(contactForm) });
      setShowContact(false);
      setContactForm({ name: '', contactTitle: '', email: '', phone: '', linkedinUrl: '' });
      load();
    } catch (e: any) { setError(e.message); }
    setSaving(false);
  };

  const removeContact = async (personId: number) => {
    if (!token || !client) return;
    await atsFetch(token, `/clients/${client.id}/contacts/${personId}`, { method: 'DELETE' }).catch(() => {});
    load();
  };

  const deleteClient = async () => {
    if (!token || !client) return;
    const typed = window.prompt(`This permanently deletes ${client.name} and ALL its roles and pipelines (placement history is kept). Type the client name to confirm:`);
    if (!typed || typed.trim().toLowerCase() !== client.name.trim().toLowerCase()) return;
    await atsFetch(token, `/clients/${client.id}`, { method: 'DELETE' }).catch((e: any) => setError(e.message));
    navigate('/ats/clients');
  };

  if (error && !client) return <p className="text-sm text-red-600">{error}</p>;
  if (!client) return <div className="py-16 text-center text-gray-400 text-sm">Loading…</div>;

  const site = company?.website || (client.domain ? `https://${client.domain}` : null);
  const location = [company?.locality || client.location, company?.country].filter(Boolean).join(', ');

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-start gap-3 min-w-0">
          {company?.logo_url ? (
            <img src={company.logo_url} alt="" className="w-12 h-12 rounded-lg object-contain bg-white ring-1 ring-gray-200 flex-shrink-0" />
          ) : (
            <span className="w-12 h-12 rounded-lg bg-gray-100 text-gray-400 flex items-center justify-center text-lg font-semibold flex-shrink-0">
              {client.name.slice(0, 1).toUpperCase()}
            </span>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs text-gray-400 mb-0.5">
              <Link to="/ats/clients" className="hover:text-gray-600">Clients</Link>
              <span>/</span>
              <span className="text-gray-500 truncate">{client.name}</span>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-lg font-semibold text-gray-900">{client.name}</h2>
              <select
                value={client.stage}
                onChange={(e) => patchClient({ stage: e.target.value })}
                className="text-xs border border-gray-200 rounded-md px-2 py-1 text-gray-600"
              >
                {CLIENT_STAGES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs">
              {site && <a href={site} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700">{client.domain || 'Website'} ↗</a>}
              {company?.linkedin_url && <a href={company.linkedin_url} target="_blank" rel="noreferrer" className="text-blue-600 hover:text-blue-700">LinkedIn ↗</a>}
              {location && <span className="text-gray-400">{location}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {company && (
            <Link to={`/project/${company.project_id}`} className="text-[11px] bg-emerald-50 text-emerald-700 ring-1 ring-inset ring-emerald-600/20 rounded-lg px-2.5 py-1.5 hover:bg-emerald-100">
              Mapped in <span className="font-semibold">{company.project_name}</span> ↗
            </Link>
          )}
          <button onClick={deleteClient} className="text-xs text-gray-400 hover:text-red-600">Delete</button>
        </div>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        {/* Main column */}
        <div className="lg:col-span-2">
          <div className="border-b border-gray-200 mb-5">
            <nav className="flex gap-1 -mb-px">
              {TABS.map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 ${tab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
                >
                  {t}
                  {t !== 'Notes' && (
                    <span className="ml-1 text-[10px] text-gray-400">
                      {t === 'Roles' ? roles.length : t === 'Contacts' ? contacts.length : placements.length}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>

          {tab === 'Roles' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Roles</h3>
                <button onClick={() => setShowRole(true)} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ New role</button>
              </div>
              <div className="px-5 py-2">
                {roles.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4">No roles yet — open one to start a pipeline.</p>
                ) : (
                  <ul className="divide-y divide-gray-50">
                    {roles.map((r) => (
                      <li key={r.id} className="py-2.5 flex items-center gap-3">
                        <Link to={`/ats/roles/${r.id}`} className="font-medium text-sm text-gray-900 hover:text-blue-700 flex-1 truncate">{r.title}</Link>
                        <StatusBadge value={r.status} />
                        <span className="text-xs text-gray-400">{r.active_cards || 0} in pipeline{(r.placed_cards || 0) > 0 ? ` · ${r.placed_cards} placed` : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'Contacts' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
              <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
                <h3 className="text-sm font-semibold text-gray-800">Contacts</h3>
                <button onClick={() => setShowContact(true)} className="text-xs font-medium text-emerald-700 hover:text-emerald-800">+ Add contact</button>
              </div>
              <div className="px-5 py-2">
                {contacts.length === 0 ? (
                  <p className="text-sm text-gray-400 py-4">No contacts logged — hiring managers and HR land here.</p>
                ) : (
                  <ul className="divide-y divide-gray-50">
                    {contacts.map((p) => (
                      <li key={p.id} className="py-2.5 flex items-center gap-3 group">
                        <div className="flex-1 min-w-0">
                          <Link to={`/ats/people/${p.id}`} className="text-sm font-medium text-gray-900 hover:text-blue-700">
                            {p.name}{p.is_primary && <span className="ml-1.5 text-[10px] text-amber-600 font-semibold">PRIMARY</span>}
                          </Link>
                          <p className="text-xs text-gray-400 truncate">{p.contact_title || p.title || '—'}{p.email ? ` · ${p.email}` : ''}{p.phone ? ` · ${p.phone}` : ''}</p>
                        </div>
                        <button onClick={() => removeContact(p.id)} className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 text-xs">✕</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {tab === 'Notes' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4">
              <NotesPanel entityType="client" entityId={client.id} />
            </div>
          )}

          {tab === 'Placements' && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-3">
              {placements.length === 0 ? (
                <p className="text-sm text-gray-400 py-4">No placements at {client.name} yet.</p>
              ) : (
                <ul className="divide-y divide-gray-50">
                  {placements.map((pl) => (
                    <li key={pl.id} className="py-2.5 flex items-center gap-3 text-sm">
                      <span className="text-gray-800">{pl.person_name}</span>
                      <span className="text-gray-400 flex-1 truncate">{pl.role_title}</span>
                      <span className="text-xs text-gray-400">{fmtDate(pl.start_date)}</span>
                      <span className="text-xs font-medium text-emerald-600">{fmtMoney(pl.fee_amount, pl.currency)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>

        {/* Right panel: Details | Activity */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm self-start lg:sticky lg:top-4">
          <div className="border-b border-gray-100 px-4">
            <nav className="flex gap-1 -mb-px">
              {(['Details', 'Activity'] as const).map((t) => (
                <button
                  key={t}
                  onClick={() => setPanelTab(t)}
                  className={`px-3 py-2.5 text-xs font-semibold border-b-2 ${panelTab === t ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-gray-400 hover:text-gray-700'}`}
                >
                  {t}
                </button>
              ))}
            </nav>
          </div>
          <div className="px-4 py-3 max-h-[70vh] overflow-y-auto">
            {panelTab === 'Details' ? (
              <div className="divide-y divide-gray-50">
                <DetailRow label="Industry">{[company?.industry, company?.subindustry].filter(Boolean).join(' · ') || '—'}</DetailRow>
                <DetailRow label="Size">{company?.company_size_formatted || (company?.employee_count ? `${company.employee_count.toLocaleString()} employees` : '—')}</DetailRow>
                {company?.founded && <DetailRow label="Founded">{company.founded}</DetailRow>}
                {company?.annual_revenue && <DetailRow label="Annual revenue">{company.annual_revenue}</DetailRow>}
                {company?.funding_range && <DetailRow label="Funding">{company.funding_range}</DetailRow>}
                <DetailRow label="Location">{location || '—'}</DetailRow>
                {company?.follower_count ? <DetailRow label="LinkedIn followers">{company.follower_count.toLocaleString()}</DetailRow> : null}
                {specialties.length > 0 && (
                  <DetailRow label="Specialties">
                    <span className="flex flex-wrap gap-1 mt-0.5">
                      {specialties.map((s) => <span key={s} className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 text-[11px]">{s}</span>)}
                    </span>
                  </DetailRow>
                )}
                {company?.description && (
                  <DetailRow label="About">
                    <span className="text-xs text-gray-500">{company.description.slice(0, 400)}{company.description.length > 400 ? '…' : ''}</span>
                  </DetailRow>
                )}
                {!company && (
                  <p className="py-2 text-[11px] text-gray-400">
                    No mapped company matched yet — enriched details attach automatically when this client's domain matches a company in your projects (and, post-dataset, from the universe).
                  </p>
                )}
                <DetailRow label="Added">{fmtDate(client.created_at)}</DetailRow>
                <div className="pt-3">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Tasks</p>
                  <TasksPanel entityType="client" entityId={client.id} />
                </div>
              </div>
            ) : (
              <TimelinePanel entityType="client" entityId={client.id} />
            )}
          </div>
        </div>
      </div>

      {showRole && (
        <Modal title={`New role at ${client.name}`} onClose={() => setShowRole(false)}>
          <div className="space-y-3">
            <Field label="Title *"><input value={roleForm.title} onChange={(e) => setRoleForm({ ...roleForm, title: e.target.value })} className={inputCls} autoFocus /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Location"><input value={roleForm.location} onChange={(e) => setRoleForm({ ...roleForm, location: e.target.value })} className={inputCls} /></Field>
              <Field label="Type">
                <select value={roleForm.employmentType} onChange={(e) => setRoleForm({ ...roleForm, employmentType: e.target.value })} className={inputCls}>
                  <option value="permanent">Permanent</option>
                  <option value="contract">Contract</option>
                  <option value="temp">Temp</option>
                  <option value="fixed_term">Fixed term</option>
                </select>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Salary min"><input type="number" value={roleForm.salaryMin} onChange={(e) => setRoleForm({ ...roleForm, salaryMin: e.target.value })} className={inputCls} /></Field>
              <Field label="Salary max"><input type="number" value={roleForm.salaryMax} onChange={(e) => setRoleForm({ ...roleForm, salaryMax: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fee type">
                <select value={roleForm.feeType} onChange={(e) => setRoleForm({ ...roleForm, feeType: e.target.value })} className={inputCls}>
                  <option value="percent">% of salary</option>
                  <option value="fixed">Fixed fee</option>
                </select>
              </Field>
              <Field label={roleForm.feeType === 'percent' ? 'Fee %' : 'Fee amount'}>
                <input type="number" value={roleForm.feeValue} onChange={(e) => setRoleForm({ ...roleForm, feeValue: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Status">
              <select value={roleForm.status} onChange={(e) => setRoleForm({ ...roleForm, status: e.target.value })} className={inputCls}>
                {ROLE_STATUSES.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Description / spec"><textarea value={roleForm.description} onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })} rows={3} className={inputCls} /></Field>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowRole(false)} className={btnGhost}>Cancel</button>
              <button onClick={createRole} disabled={saving || !roleForm.title.trim()} className={btnPrimary}>{saving ? 'Creating…' : 'Create role'}</button>
            </div>
          </div>
        </Modal>
      )}

      {showContact && (
        <Modal title={`Add contact at ${client.name}`} onClose={() => setShowContact(false)}>
          <div className="space-y-3">
            <Field label="Name *"><input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })} className={inputCls} autoFocus /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Job title"><input value={contactForm.contactTitle} onChange={(e) => setContactForm({ ...contactForm, contactTitle: e.target.value })} className={inputCls} /></Field>
              <Field label="Email"><input value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })} className={inputCls} /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Phone"><input value={contactForm.phone} onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} className={inputCls} /></Field>
              <Field label="LinkedIn URL"><input value={contactForm.linkedinUrl} onChange={(e) => setContactForm({ ...contactForm, linkedinUrl: e.target.value })} className={inputCls} /></Field>
            </div>
            <p className="text-[11px] text-gray-400">If this person already exists (matched by email, phone or LinkedIn), the contact role is added to their existing record — no duplicate is created.</p>
            <div className="flex justify-end gap-2 pt-1">
              <button onClick={() => setShowContact(false)} className={btnGhost}>Cancel</button>
              <button onClick={addContact} disabled={saving || !contactForm.name.trim()} className={btnPrimary}>{saving ? 'Adding…' : 'Add contact'}</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AtsClientDetail;
